import { gql } from 'graphql-tag';

export const integrationInfoFragment = gql`
  fragment IntegrationInfo on TelematicsIntegration {
    telematics_integration_id
    telematics_provider_id
    company_integration_id
    company_id
    connection_data
    enabled
    diagnosticStatus
    diagnosticComment
    createdAt
    updatedAt
    isImmutable
    isDeletable
    integrationApiDebugUrl
    integrationApiDebugWithSyncStateUrl
    totalTrucksCount
    activeTrucksCount
    recentAllocationsCount

    telematicsProvider {
      telematicsProviderId
      telematicsProviderName
      isVehicleManagementEnabled
      isDeprecated

      connectionParameters(includeInternal: true) {
        fieldName
        fieldType
        isInternal
        isOptional
        isSensitive
        fieldDisplayName
        enumFieldValues {
          value
          display_name
          is_default
        }
      }
    }

    company {
      company_id
      company_name
    }

    createdBy {
      user_id
      first_name
      last_name
    }

    updatedBy {
      user_id
      first_name
      last_name
    }

    integrationSyncHistory {
      startTimeAt
      endTimeAt
      vehicleCount
      statusCount
      temperatureCount
      distinctVehicleStatusFutureCount
      distinctVehicleTemperatureFutureCount
      distinctVehicleNullIslandCount
      vehicleAmbiguityCount
      latestStatusTimestamp
      latestTemperatureTimestamp
      errorDescription
      errorType
      resultType
      conflictingLicensePlates {
        licensePlate
        remoteIds
      }
      conflictingRemoteIds {
        remoteId
        licensePlates
      }
      integrationType
      tourPositionsCount
      stopEventsCount
      tourEventsCount
      tourAllocationsCount
    }
  }
`;

export const telematicsProvidersListQuery = gql`
  query TelematicsProvidersList(
    $limit: Int
    $cursor: String
    $isAddingIntegrationDisabled: Boolean
    $isInternalUseOnly: Boolean
    $isExternalConfigurationNeeded: Boolean
    $isGlobalAllocationForbidden: Boolean
    $isVehicleManagementEnabled: Boolean
    $isTourBasedProvider: Boolean
    $integrationType: String
    $isDeprecated: Boolean
    $textSearch: String
    $sort: TelematicsProvidersSortInput
  ) {
    telematicsProvidersV2(
      isAddingIntegrationDisabled: $isAddingIntegrationDisabled
      isInternalUseOnly: $isInternalUseOnly
      isExternalConfigurationNeeded: $isExternalConfigurationNeeded
      isGlobalAllocationForbidden: $isGlobalAllocationForbidden
      isVehicleManagementEnabled: $isVehicleManagementEnabled
      isTourBasedProvider: $isTourBasedProvider
      integrationType: $integrationType
      isDeprecated: $isDeprecated
      textSearch: $textSearch
      sort: $sort
    ) {
      count
      providers(first: $limit, after: $cursor) {
        edges {
          node {
            telematicsProviderId
            telematicsProviderName
            totalTrucksCount
            activeTrucksCount
            integrationInstructionsURL
            isAddingIntegrationDisabled
            isInternalUseOnly
            isExternalConfigurationNeeded
            isGlobalAllocationForbidden
            isVehicleManagementEnabled
            isTourBasedProvider
            integrationType
            isDeprecated
            recentAllocationsCount

            telematicsIntegrations {
              count
            }

            enabledIntegrations: telematicsIntegrations(isEnabled: true) {
              count
            }
          }
        }

        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const telematicsProvidersSimpleListQuery = gql`
  query TelematicsProvidersSimpleList {
    telematicsProvidersV2 {
      providers(first: 1500) {
        edges {
          node {
            telematicsProviderId
            telematicsProviderName
            isAddingIntegrationDisabled
            isDeprecated

            connectionParameters(includeInternal: true) {
              fieldName
              fieldType
              isInternal
              isOptional
              isSensitive
              fieldDisplayName
              enumFieldValues {
                value
                display_name
                is_default
              }
            }
          }
        }
      }
    }
  }
`;

export const telematicsProviderQuery = gql`
  ${integrationInfoFragment}

  query TelematicsProvider(
    $telematics_provider_id: String!
    $cursor: String
    $first: Int
    $sort: TelematicsIntegrationsSortInput
  ) {
    telematicsProvider(telematics_provider_id: $telematics_provider_id) {
      telematicsProviderId
      telematicsProviderName
      integrationInstructionsURL
      isAddingIntegrationDisabled
      isInternalUseOnly
      isExternalConfigurationNeeded
      isVehicleManagementEnabled
      isGlobalAllocationForbidden
      isDeprecated
      integrationType
      isTourBasedProvider
      totalTrucksCount
      activeTrucksCount
      recentAllocationsCount
      contact_info {
        email_address
        comment
        updated_at
      }

      connectionParameters(includeInternal: true) {
        fieldName
        fieldType
        isInternal
        isOptional
        isSensitive
        fieldDisplayName
        enumFieldValues {
          value
          display_name
          is_default
        }
      }

      telematicsIntegrations(sort: $sort) {
        count
        integrations(after: $cursor, first: $first) {
          edges {
            node {
              ...IntegrationInfo
            }
          }

          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      enabledIntegrations: telematicsIntegrations(isEnabled: true) {
        count
      }
    }
  }
`;

export const telematicsIntegrationQuery = gql`
  ${integrationInfoFragment}

  query TelematicsIntegration($telematics_integration_id: String!) {
    telematicsIntegration(telematics_integration_id: $telematics_integration_id) {
      ...IntegrationInfo
    }
  }
`;

export const updateTelematicsProviderMutation = gql`
  mutation UpdateTelematicsProvider($input: UpdateTelematicsProviderInput!) {
    updateTelematicsProvider(input: $input) {
      contact_info {
        email_address
        comment
        updated_at
      }
    }
  }
`;

export const updateTelematicsIntegrationMutation = gql`
  ${integrationInfoFragment}

  mutation UpdateTelematicsIntegration($input: UpdateTelematicsIntegrationInput!) {
    updateTelematicsIntegration(input: $input) {
      company {
        company_id
        company_name
      }
      ...IntegrationInfo
    }
  }
`;

export const createTelematicsIntegrationMutation = gql`
  ${integrationInfoFragment}

  mutation CreateTelematicsIntegration($input: CreateTelematicsIntegrationInput!) {
    createTelematicsIntegration(input: $input) {
      company {
        company_id
        company_name
      }
      ...IntegrationInfo
    }
  }
`;

export const deleteTelematicsIntegrationMutation = gql`
  mutation DeleteTelematicsIntegration($telematics_integration_id: String!) {
    deleteTelematicsIntegration(telematics_integration_id: $telematics_integration_id)
  }
`;

export const deleteTelematicsIntegrationSyncStateMutation = gql`
  mutation DeleteTelematicsIntegrationSyncState($telematics_integration_id: String!) {
    deleteTelematicsIntegrationSyncState(telematics_integration_id: $telematics_integration_id)
  }
`;

export const updateTelematicsIntegrationSyncStateNextSyncAtMutation = gql`
  mutation UpdateTelematicsIntegrationSyncStateNextSyncAt($telematics_integration_id: String!) {
    updateTelematicsIntegrationSyncStateNextSyncAt(telematics_integration_id: $telematics_integration_id)
  }
`;

export const telematicsIntegrationsListQuery = gql`
  ${integrationInfoFragment}

  query TelematicsIntegrationsList(
    $limit: Int
    $cursor: String
    $textSearch: String
    $companyId: String
    $sort: TelematicsIntegrationsSortInput
    $isEnabled: Boolean
  ) {
    telematicsIntegrations(textSearch: $textSearch, companyId: $companyId, sort: $sort, isEnabled: $isEnabled) {
      count
      integrations(first: $limit, after: $cursor) {
        edges {
          node {
            company_id
            ...IntegrationInfo
          }
        }

        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const telematicsCountsQuery = gql`
  query TelematicsCountsQuery {
    telematicsProvidersV2 {
      count
    }
    telematicsIntegrations {
      count
    }
  }
`;

export const vehicleTrackerByTelematicsIntegrationAndVehicleDetailsQuery = gql`
  query VehicleTrackerByTelematicsIntegrationAndVehicleDetails(
    $telematicsIntegrationId: String!
    $remoteId: String
    $licensePlate: String
  ) {
    vehicleTrackerByTelematicsIntegrationAndVehicleDetails(
      telematicsIntegrationId: $telematicsIntegrationId
      remoteId: $remoteId
      licensePlate: $licensePlate
    ) {
      vehicleTrackerId
    }
  }
`;

export const sensitiveCliCommandDataQuery = gql`
  query SensitiveCliCommandData($telematics_integration_id: String!, $captcha_token: String!) {
    sensitiveCliCommandData(telematics_integration_id: $telematics_integration_id, captcha_token: $captcha_token)
  }
`;

export const webhookSubscriptionFragment = gql`
  fragment WebhookSubscriptionInfo on CompanyWebhookSubscriptionInfo {
    subscriptionId
    companyId
    url
    topics
    headers
    status
    contactEmails
    payloadContentType
    expirationDate
    lastStatusChange
    subscriptionUpdatedAt
    createdAt
    updatedAt
    brokenReason
    discriminator
    createdByUserId
    updatedByUserId
  }
`;

export const companyWebhookSubscriptionQuery = gql`
  ${webhookSubscriptionFragment}

  query CompanyWebhookSubscription($company_id: String!, $subscription_id: String!) {
    company(company_id: $company_id) {
      company_id
      companyWebhookSubscription(subscriptionId: $subscription_id) {
        ...WebhookSubscriptionInfo
      }
    }
  }
`;

export const updateWebhookSubscriptionMutation = gql`
  ${webhookSubscriptionFragment}

  mutation UpdateWebhookSubscription($input: UpdateWebhookSubscriptionInput!) {
    updateWebhookSubscription(input: $input) {
      ...WebhookSubscriptionInfo
    }
  }
`;

export const createWebhookSubscriptionMutation = gql`
  ${webhookSubscriptionFragment}

  mutation CreateWebhookSubscription($input: CreateWebhookSubscriptionInput!) {
    createWebhookSubscription(input: $input) {
      ...WebhookSubscriptionInfo
    }
  }
`;

export const deleteWebhookSubscriptionMutation = gql`
  mutation DeleteWebhookSubscription($input: DeleteWebhookSubscriptionInput!) {
    deleteWebhookSubscription(input: $input)
  }
`;
